// src/api/authSession.js
import { resolveLobbyApiBase } from '../config/serviceUrl';

const API_BASE = resolveLobbyApiBase();
const REFRESH_BEFORE_MS = Math.max(60_000, Number(process.env.REACT_APP_AUTH_REFRESH_BEFORE_MS || 10 * 60 * 1000));
const AUTH_REFRESH_TIMEOUT_MS = Math.max(2500, Number(process.env.REACT_APP_AUTH_REFRESH_TIMEOUT_MS || 6000) || 6000);

const DEFINITIVE_AUTH_ERRORS = new Set([
  'NO_TOKEN',
  'SESSION_MISSING',
  'SESSION_EXPIRED',
  'SESSION_MISMATCH',
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_REVOKED',
  'ACCOUNT_NOT_FOUND',
]);

let refreshPromise = null;

export function getStoredAccessToken() {
  try {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('authToken') ||
      ''
    );
  } catch (_) {
    return '';
  }
}

function decodeJwtPayload(token = '') {
  try {
    const part = String(token || '').split('.')[1] || '';
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export function getAccessTokenRemainingMs(token = getStoredAccessToken()) {
  const payload = decodeJwtPayload(token);
  const expMs = Number(payload?.exp || 0) * 1000;
  if (!expMs) return Number.POSITIVE_INFINITY;
  return expMs - Date.now();
}

function buildRefreshError(status, data = {}, cause = null) {
  const err = new Error('AUTH_REFRESH_FAILED');
  err.status = Number(status || 0);
  err.code = String(data?.error || '').trim();
  err.data = data || {};
  err.cause = cause || undefined;
  err.transient = err.status === 0 || err.status >= 500 || err.code === 'SESSION_STORE_UNAVAILABLE';
  return err;
}

export function isDefinitiveAuthFailure(error) {
  const code = String(error?.code || error?.response?.data?.error || error?.message || '').trim();
  const status = Number(error?.status || error?.response?.status || 0);
  if (code === 'SESSION_STORE_UNAVAILABLE' || status >= 500 || status === 0) return false;
  return status === 401 && DEFINITIVE_AUTH_ERRORS.has(code);
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const currentToken = getStoredAccessToken();
    if (!currentToken) throw buildRefreshError(401, { error: 'NO_TOKEN' });

    let response;
    let data = {};
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => controller.abort(), AUTH_REFRESH_TIMEOUT_MS)
        : null;
      try {
        response = await fetch(`${String(API_BASE || '').replace(/\/$/, '')}/user/session/refresh`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
          cache: 'no-store',
          credentials: 'same-origin',
          ...(controller ? { signal: controller.signal } : {}),
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
      try { data = await response.json(); } catch (_) { data = {}; }
    } catch (cause) {
      throw buildRefreshError(0, { error: 'AUTH_REFRESH_NETWORK_ERROR' }, cause);
    }

    if (!response.ok || !data?.token) {
      throw buildRefreshError(response.status, data);
    }

    const nextToken = String(data.token || '').trim();
    try { localStorage.setItem('token', nextToken); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('npc:auth-token-refreshed', {
        detail: { token: nextToken, session_id: data?.session_id || '', ts: Date.now() },
      }));
    } catch (_) {}
    return nextToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function ensureFreshAccessToken(options = {}) {
  const token = getStoredAccessToken();
  if (!token) return '';
  const minValidityMs = Math.max(0, Number(options.minValidityMs ?? REFRESH_BEFORE_MS));
  const remainingMs = getAccessTokenRemainingMs(token);
  if (options.force !== true && remainingMs > minValidityMs) return token;
  return refreshAccessToken();
}
